import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

// Our target countries (ISO alpha-2 lowercase)
const TARGET_COUNTRIES = new Set([
  'cz','sk','de','at','pl','fr','it','ch','hu','se','no','fi',
  'dk','nl','be','gb','hr','si','ro','es','pt','ie','lt','lv','ee',
]);

const countryNames: Record<string, { cs: string; en: string }> = {
  cz: { cs: 'Česká republika', en: 'Czech Republic' },
  sk: { cs: 'Slovensko', en: 'Slovakia' },
  de: { cs: 'Německo', en: 'Germany' },
  at: { cs: 'Rakousko', en: 'Austria' },
  pl: { cs: 'Polsko', en: 'Poland' },
  fr: { cs: 'Francie', en: 'France' },
  it: { cs: 'Itálie', en: 'Italy' },
  ch: { cs: 'Švýcarsko', en: 'Switzerland' },
  hu: { cs: 'Maďarsko', en: 'Hungary' },
  se: { cs: 'Švédsko', en: 'Sweden' },
  no: { cs: 'Norsko', en: 'Norway' },
  fi: { cs: 'Finsko', en: 'Finland' },
  dk: { cs: 'Dánsko', en: 'Denmark' },
  nl: { cs: 'Nizozemsko', en: 'Netherlands' },
  be: { cs: 'Belgie', en: 'Belgium' },
  gb: { cs: 'Velká Británie', en: 'United Kingdom' },
  hr: { cs: 'Chorvatsko', en: 'Croatia' },
  si: { cs: 'Slovinsko', en: 'Slovenia' },
  ro: { cs: 'Rumunsko', en: 'Romania' },
  es: { cs: 'Španělsko', en: 'Spain' },
  pt: { cs: 'Portugalsko', en: 'Portugal' },
  ie: { cs: 'Irsko', en: 'Ireland' },
  lt: { cs: 'Litva', en: 'Lithuania' },
  lv: { cs: 'Lotyšsko', en: 'Latvia' },
  ee: { cs: 'Estonsko', en: 'Estonia' },
};

// Map IDs in the SVG that need grouping (e.g. gb-gbn, gb-nir → gb)
const ID_MAPPING: Record<string, string> = {
  'gb-gbn': 'gb',
  'gb-nir': 'gb',
  'ru-kgd': 'ru',
  'ru-main': 'ru',
};

interface EuropeMapProps {
  activeCountries: string[];
}

const EuropeMap = memo(({ activeCountries }: EuropeMapProps) => {
  const { lang } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [tooltipInfo, setTooltipInfo] = useState<{ x: number; y: number; code: string } | null>(null);
  const [svgLoaded, setSvgLoaded] = useState(false);

  const getCountryCode = useCallback((el: Element): string | null => {
    const id = el.id?.toLowerCase();
    if (!id) return null;
    if (ID_MAPPING[id]) return ID_MAPPING[id];
    return id;
  }, []);

  const applyStyles = useCallback((svgEl: SVGSVGElement) => {
    const paths = svgEl.querySelectorAll('path');
    paths.forEach((path) => {
      const code = getCountryCode(path);
      if (!code) {
        path.style.fill = 'hsl(var(--foreground) / 0.04)';
        path.style.stroke = 'hsl(var(--foreground) / 0.08)';
        path.style.strokeWidth = '0.3';
        return;
      }

      const isCZ = code === 'cz';
      const isActive = activeCountries.includes(code);
      const isTarget = TARGET_COUNTRIES.has(code);
      const isHovered = hoveredCountry === code;

      if (isCZ) {
        path.style.fill = 'hsl(var(--primary))';
        path.style.stroke = 'hsl(var(--primary))';
        path.style.strokeWidth = '2';
        path.style.filter = 'drop-shadow(0 0 10px hsl(var(--primary) / 0.6))';
      } else if (isHovered && isActive) {
        path.style.fill = 'hsl(var(--primary) / 0.7)';
        path.style.stroke = 'hsl(var(--primary) / 0.6)';
        path.style.strokeWidth = '1.5';
        path.style.filter = 'drop-shadow(0 0 6px hsl(var(--primary) / 0.4))';
      } else if (isActive) {
        path.style.fill = 'hsl(var(--primary) / 0.4)';
        path.style.stroke = 'hsl(var(--primary) / 0.3)';
        path.style.strokeWidth = '0.8';
        path.style.filter = 'none';
      } else if (isHovered && isTarget) {
        path.style.fill = 'hsl(var(--foreground) / 0.15)';
        path.style.stroke = 'hsl(var(--primary) / 0.4)';
        path.style.strokeWidth = '1';
        path.style.filter = 'none';
      } else {
        path.style.fill = 'hsl(var(--foreground) / 0.06)';
        path.style.stroke = 'hsl(var(--foreground) / 0.1)';
        path.style.strokeWidth = '0.5';
        path.style.filter = 'none';
      }

      path.style.transition = 'fill 0.2s ease, stroke 0.2s ease, filter 0.2s ease';
      path.style.cursor = isTarget ? 'pointer' : 'default';
    });
  }, [activeCountries, hoveredCountry, getCountryCode]);

  // Load SVG
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    fetch('/europe-map.svg')
      .then(res => res.text())
      .then(svgText => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const svgEl = doc.querySelector('svg');
        if (!svgEl) return;

        // Remove any existing style/rect/metadata
        svgEl.querySelectorAll('style, metadata, desc, title, defs').forEach(el => el.remove());
        const ocean = svgEl.querySelector('#ocean');
        if (ocean) ocean.remove();

        // Set responsive sizing
        svgEl.setAttribute('width', '100%');
        svgEl.setAttribute('height', 'auto');
        svgEl.style.display = 'block';

        // Remove background rects
        svgEl.querySelectorAll('rect').forEach(r => r.remove());

        container.innerHTML = '';
        container.appendChild(svgEl);
        setSvgLoaded(true);
      });
  }, []);

  // Apply styles when dependencies change
  useEffect(() => {
    if (!svgLoaded || !containerRef.current) return;
    const svgEl = containerRef.current.querySelector('svg');
    if (svgEl) applyStyles(svgEl as SVGSVGElement);
  }, [svgLoaded, activeCountries, hoveredCountry, applyStyles]);

  // Mouse events
  useEffect(() => {
    if (!svgLoaded || !containerRef.current) return;
    const svgEl = containerRef.current.querySelector('svg');
    if (!svgEl) return;

    const handleMouseEnter = (e: Event) => {
      const target = e.target as SVGPathElement;
      const code = getCountryCode(target);
      if (code && TARGET_COUNTRIES.has(code)) {
        setHoveredCountry(code);
      }
    };

    const handleMouseLeave = () => {
      setHoveredCountry(null);
      setTooltipInfo(null);
    };

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const target = mouseEvent.target as SVGPathElement;
      const code = getCountryCode(target);
      if (code && TARGET_COUNTRIES.has(code) && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setTooltipInfo({
          x: mouseEvent.clientX - rect.left,
          y: mouseEvent.clientY - rect.top - 10,
          code,
        });
      }
    };

    const paths = svgEl.querySelectorAll('path');
    paths.forEach(path => {
      path.addEventListener('mouseenter', handleMouseEnter);
      path.addEventListener('mouseleave', handleMouseLeave);
      path.addEventListener('mousemove', handleMouseMove);
    });

    return () => {
      paths.forEach(path => {
        path.removeEventListener('mouseenter', handleMouseEnter);
        path.removeEventListener('mouseleave', handleMouseLeave);
        path.removeEventListener('mousemove', handleMouseMove);
      });
    };
  }, [svgLoaded, getCountryCode]);

  const getLabel = (code: string) => {
    if (code === 'cz') return lang === 'cs' ? '✦ Domovská země' : '✦ Home country';
    if (activeCountries.includes(code)) return lang === 'cs' ? '📦 Dodáváme sem' : '📦 We deliver here';
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="relative w-full max-w-4xl mx-auto"
    >
      <div ref={containerRef} className="w-full" />

      {/* Custom tooltip */}
      {tooltipInfo && countryNames[tooltipInfo.code] && (
        <div
          className="absolute z-50 pointer-events-none px-3 py-2 rounded-lg backdrop-blur-md bg-popover/90 border border-primary/20 shadow-lg"
          style={{
            left: tooltipInfo.x,
            top: tooltipInfo.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <p className="font-semibold text-sm text-foreground">
            {countryNames[tooltipInfo.code][lang]}
          </p>
          {getLabel(tooltipInfo.code) && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {getLabel(tooltipInfo.code)}
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
});

EuropeMap.displayName = 'EuropeMap';

export default EuropeMap;
